// ============================================================
// CAMERA WALL — Fase 9.3
// Grid multi-câmera com status realtime, overlays, auto-rotation
// Visual: NOC industrial enterprise
// ============================================================
import { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Grid2X2, Maximize2, Minimize2, RotateCcw, Filter,
  Bell, AlertTriangle, Camera, Zap,
} from 'lucide-react'
import { useVisionStore, selectActiveEvents, selectPendingViolations } from '@/store/visionStore'
import { CameraCard, StreamStatus, DetectionBadge, VisionEventRow } from '@/design-system/vision'
import { TEXT, SURFACE } from '@/design-system/tokens'
import { FF } from '@/core/featureFlags'
import type { CameraDevice } from '@/types/vision'

type GridSize = 1 | 2 | 4 | 6 | 9

const GRID_COLS: Record<GridSize, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  4: 'grid-cols-2',
  6: 'grid-cols-3',
  9: 'grid-cols-3',
}

interface Props {
  obraId:   string
  onSelect?: (camera: CameraDevice) => void
}

export default function CameraWall({ obraId, onSelect }: Props) {
  const {
    cameras, selectedCameraId,
    selectCamera, resolveEvent, acknowledgeViolation,
  } = useVisionStore()

  const [gridSize, setGridSize]       = useState<GridSize>(4)
  const [fullscreen, setFullscreen]   = useState(false)
  const [showEvents, setShowEvents]   = useState(true)
  const [autoRotate, setAutoRotate]   = useState(false)
  const [rotateIndex, setRotateIndex] = useState(0)
  const [filterStatus, setFilterStatus] = useState<'all' | 'online' | 'offline'>('all')

  const autoRotateRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // FIX PROB-15: Auto-rotation with proper cleanup — no interval accumulation
  useEffect(() => {
    // Always clear existing interval first (even if autoRotate is now false)
    if (autoRotateRef.current) {
      clearInterval(autoRotateRef.current)
      autoRotateRef.current = null
    }
    if (!autoRotate || cameras.length === 0) return

    autoRotateRef.current = setInterval(() => {
      setRotateIndex(i => (i + gridSize) % cameras.length)
    }, 8000)

    return () => {
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current)
        autoRotateRef.current = null
      }
    }
  }, [autoRotate, cameras.length, gridSize])

  const filteredCameras = cameras.filter(c => {
    if (c.obra_id !== obraId) return false
    if (filterStatus === 'online')  return c.status === 'online'
    if (filterStatus === 'offline') return c.status !== 'online'
    return true
  })

  const visibleCameras = autoRotate
    ? filteredCameras.slice(rotateIndex, rotateIndex + gridSize)
    : filteredCameras

  // FIX: use store selector properly instead of manual invocation with cast
  const activeEvents      = useVisionStore(selectActiveEvents)
  const pendingViolations = useVisionStore(selectPendingViolations)

  const getAlertCount = useCallback((cameraId: string) =>
    activeEvents.filter(e => e.camera_id === cameraId).length +
    pendingViolations.filter(v => v.camera_id === cameraId).length,
  [activeEvents, pendingViolations])

  const totalAlerts = activeEvents.length + pendingViolations.length

  return (
    <div className={`flex flex-col ${fullscreen ? 'fixed inset-0 z-50' : 'h-full'}`}
      style={{ background: 'hsl(222 25% 5%)' }}>

      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-4 py-3 shrink-0"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.3)' }}>

        <div className="flex items-center gap-2">
          <Camera size={14} style={{ color: 'hsl(24 95% 55%)' }} />
          <span className="text-sm font-black uppercase tracking-widest text-white">Camera Wall</span>
        </div>

        {/* Status counts */}
        <div className="flex items-center gap-3 text-[10px]">
          <span style={{ color: TEXT.faint }}>
            {filteredCameras.filter(c => c.status === 'online').length}/
            {filteredCameras.length} online
          </span>
          {totalAlerts > 0 && (
            <motion.div
              animate={{ opacity: [1, 0.5, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="flex items-center gap-1 px-2 py-0.5 rounded-full"
              style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171' }}>
              <Bell size={9} />
              <span className="font-black">{totalAlerts}</span>
            </motion.div>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Filter */}
          <div className="flex gap-1">
            {(['all', 'online', 'offline'] as const).map(f => (
              <button key={f} onClick={() => setFilterStatus(f)}
                className="text-[9px] font-bold px-2 py-1 rounded-lg"
                style={{
                  background: filterStatus === f ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.04)',
                  color: filterStatus === f ? 'white' : TEXT.muted,
                  border: filterStatus === f ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.06)',
                }}>
                {f}
              </button>
            ))}
          </div>

          {/* Grid size */}
          <div className="flex gap-1">
            {([1, 2, 4, 6, 9] as GridSize[]).map(n => (
              <button key={n} onClick={() => setGridSize(n)}
                className="text-[9px] font-bold px-2 py-1 rounded-lg"
                style={{
                  background: gridSize === n ? 'rgba(249,115,22,0.15)' : 'rgba(255,255,255,0.04)',
                  color:      gridSize === n ? 'hsl(24 95% 55%)' : TEXT.muted,
                  border:     gridSize === n ? '1px solid rgba(249,115,22,0.3)' : '1px solid rgba(255,255,255,0.06)',
                }}>
                {n}
              </button>
            ))}
          </div>

          {/* Controls */}
          <button onClick={() => setAutoRotate(r => !r)}
            className="p-1.5 rounded-lg"
            style={{
              background: autoRotate ? 'rgba(249,115,22,0.15)' : 'rgba(255,255,255,0.04)',
              color:      autoRotate ? 'hsl(24 95% 55%)' : TEXT.muted,
              border:     '1px solid rgba(255,255,255,0.08)',
            }}>
            <RotateCcw size={12} className={autoRotate ? 'animate-spin' : ''} />
          </button>

          <button onClick={() => setShowEvents(v => !v)}
            className="p-1.5 rounded-lg"
            style={{ background: showEvents ? 'rgba(96,165,250,0.1)' : 'rgba(255,255,255,0.04)', color: showEvents ? '#60a5fa' : TEXT.muted, border: '1px solid rgba(255,255,255,0.08)' }}>
            <Bell size={12} />
          </button>

          <button onClick={() => setFullscreen(v => !v)}
            className="p-1.5 rounded-lg"
            style={{ background: 'rgba(255,255,255,0.04)', color: TEXT.muted, border: '1px solid rgba(255,255,255,0.08)' }}>
            {fullscreen ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
          </button>
        </div>
      </div>

      {/* ── Main ── */}
      <div className="flex-1 flex overflow-hidden">
        {/* Camera grid */}
        <div className="flex-1 overflow-auto p-3">
          {filteredCameras.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 opacity-30">
              <Camera size={40} />
              <span className="text-sm">Nenhuma câmera cadastrada</span>
              {!FF.ENABLE_CAMERA_WALL && (
                <span className="text-xs">Feature flag ENABLE_CAMERA_WALL está desativada</span>
              )}
            </div>
          ) : (
            <div className={`grid gap-3 ${GRID_COLS[gridSize]}`}>
              <AnimatePresence mode="popLayout">
                {visibleCameras.map(cam => (
                  <CameraCard
                    key={cam.id}
                    camera={cam}
                    isSelected={selectedCameraId === cam.id}
                    onClick={() => {
                      selectCamera(cam.id)
                      onSelect?.(cam)
                    }}
                    compact={gridSize >= 6}
                    hasAlert={getAlertCount(cam.id) > 0}
                    alertCount={getAlertCount(cam.id)}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Events sidebar */}
        <AnimatePresence>
          {showEvents && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 280, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              className="overflow-y-auto shrink-0"
              style={{ borderLeft: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.2)' }}
            >
              <div className="p-3">
                <div className="text-[9px] font-black uppercase tracking-widest mb-3 flex items-center gap-2"
                  style={{ color: TEXT.muted }}>
                  <AlertTriangle size={9} />
                  Eventos Visuais ({activeEvents.length})
                </div>

                {/* Detection badges */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <DetectionBadge
                    label="PPE"
                    count={pendingViolations.length}
                    severity={pendingViolations.length > 0 ? 'critical' : 'ok'}
                    icon={<Zap size={9} />}
                  />
                  <DetectionBadge
                    label="Movimento"
                    count={activeEvents.filter(e => e.tipo === 'motion_detected').length}
                    severity="info"
                  />
                  <DetectionBadge
                    label="Inativo"
                    count={activeEvents.filter(e => e.tipo === 'inactivity_detected').length}
                    severity={activeEvents.some(e => e.tipo === 'inactivity_detected') ? 'warning' : 'ok'}
                  />
                </div>

                {activeEvents.length === 0 ? (
                  <div className="text-center py-6 opacity-30">
                    <Camera size={20} className="mx-auto mb-2" />
                    <div className="text-xs">Sem eventos ativos</div>
                  </div>
                ) : (
                  <AnimatePresence mode="popLayout">
                    {activeEvents.slice(0, 30).map(event => (
                      <VisionEventRow
                        key={event.id}
                        event={event}
                        onResolve={resolveEvent}
                      />
                    ))}
                  </AnimatePresence>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Footer ── */}
      <div className="flex items-center gap-4 px-4 py-2 text-[9px] shrink-0"
        style={{ borderTop: '1px solid rgba(255,255,255,0.04)', background: 'rgba(0,0,0,0.3)', color: TEXT.faint }}>
        <span className="flex items-center gap-1">
          <span className="w-1 h-1 rounded-full bg-green-400 animate-pulse" />
          VISION REALTIME
        </span>
        <span>{filteredCameras.length} câmera(s)</span>
        {autoRotate && <span>🔄 Auto-rotate ativo</span>}
        <span className="ml-auto">{new Date().toLocaleTimeString('pt-BR')}</span>
      </div>
    </div>
  )
}
